import { useEffect, useMemo } from 'react';
import type { Moment } from 'moment';
import type {
  IDateCallbackData,
  TDateSourceData,
  TPickerType,
  TRangeValue,
} from './type';
import {
  applyApiDefaultFilterValue,
  checkApiDefaultDataReady,
  getApiDefaultRootKey,
  getDefaultDayRange,
  getDefaultMonthRange,
  getRangeSubtractConfig,
  initDefaultFilterValue,
  resolveApiDefaultDate,
} from './utils';

/**
 * 根据配置路径和外部数据，生成接口默认日期相关的派生信息。
 * 统一处理：
 * 1. dynamicString / 点路径解析
 * 2. 根节点 ready 判断
 * 3. 是否启用接口默认值能力
 */
export const useApiDefaultDateInfo = (
  dataPath: string | undefined,
  data: TDateSourceData | undefined,
): {
  hasApiDefaultConfig: boolean;
  apiDefaultRootKey: string;
  apiDefaultDate: string | null;
  isApiDefaultDataReady: boolean;
} => {
  const hasApiDefaultConfig = Boolean(dataPath);

  const apiDefaultRootKey = useMemo(() => {
    return getApiDefaultRootKey(dataPath);
  }, [dataPath]);

  const apiDefaultDate = useMemo(() => {
    return resolveApiDefaultDate(dataPath, data);
  }, [data, dataPath]);

  const isApiDefaultDataReady = useMemo(() => {
    return checkApiDefaultDataReady(data, apiDefaultRootKey, hasApiDefaultConfig);
  }, [apiDefaultRootKey, data, hasApiDefaultConfig]);

  return {
    hasApiDefaultConfig,
    apiDefaultRootKey,
    apiDefaultDate,
    isApiDefaultDataReady,
  };
};

/**
 * 计算月范围选择器的默认区间。
 */
export const useDefaultMonthRange = (
  dateSubtract: number,
  startSubtract: number | undefined,
  endSubtract: number | undefined,
  timeZone: number,
): [Moment, Moment] => {
  return useMemo(() => {
    const { rangeStartSubtract, rangeEndSubtract } = getRangeSubtractConfig({
      dateSubtract,
      startSubtract,
      endSubtract,
    });

    return getDefaultMonthRange(rangeStartSubtract, rangeEndSubtract, timeZone);
  }, [dateSubtract, endSubtract, startSubtract, timeZone]);
};

/**
 * 计算日区间选择器的默认天数区间。
 */
export const useDefaultDayRange = (
  dateSubtract: number,
  startSubtract: number | undefined,
  endSubtract: number | undefined,
  timeZone: number,
): [Moment, Moment] => {
  return useMemo(() => {
    const rangeEndSubtract = endSubtract ?? dateSubtract;
    const rangeStartSubtract = startSubtract ?? dateSubtract;
    return getDefaultDayRange(rangeStartSubtract, rangeEndSubtract, timeZone);
  }, [dateSubtract, endSubtract, startSubtract, timeZone]);
};

/**
 * 首屏初始化筛选器值。
 * 优先使用接口默认日期；若未配置或未解析到有效值，再退回到 dateSubtract 方案。
 * 该 hook 不维护「是否已初始化」状态，由外层通过 ref 控制，避免重复触发。
 */
export const useInitFilterValue = (
  hasInitialized: boolean,
  hasApiDefaultConfig: boolean,
  isApiDefaultDataReady: boolean,
  apiDefaultDate: string | null,
  pickerType: TPickerType,
  dateSubtract: number,
  timeZone: number,
  defaultMonthRange: [Moment, Moment],
  setTimeValue: (val: string) => void,
  setMonthValue: (val: string) => void,
  setMonthRange: (val: TRangeValue) => void,
  getData: (params: IDateCallbackData) => void,
  markInitialized: () => void,
  disabledStart = '2025-01-01',
  defaultDayRange?: [Moment, Moment],
  setDayRange?: (val: TRangeValue) => void,
  dayAsYtd = false,
  disableCurrent = false,
  disableFuture = false,
): void => {
  useEffect(() => {
    if (hasInitialized) {
      return;
    }

    if (hasApiDefaultConfig) {
      if (!isApiDefaultDataReady) {
        return;
      }

      const applied = applyApiDefaultFilterValue(
        apiDefaultDate,
        pickerType,
        setTimeValue,
        setMonthValue,
        setMonthRange,
        getData,
        timeZone,
        disabledStart,
        setDayRange,
        dayAsYtd,
        disableCurrent,
        disableFuture,
      );

      if (applied) {
        markInitialized();
        return;
      }
    }

    initDefaultFilterValue(
      pickerType,
      dateSubtract,
      timeZone,
      defaultMonthRange,
      setTimeValue,
      setMonthValue,
      setMonthRange,
      getData,
      disabledStart,
      defaultDayRange,
      setDayRange,
      dayAsYtd,
      disableCurrent,
      disableFuture,
    );
    markInitialized();
  }, [
    apiDefaultDate,
    dayAsYtd,
    dateSubtract,
    defaultDayRange,
    defaultMonthRange,
    disableCurrent,
    disableFuture,
    disabledStart,
    getData,
    hasApiDefaultConfig,
    hasInitialized,
    isApiDefaultDataReady,
    markInitialized,
    pickerType,
    setDayRange,
    setMonthRange,
    setMonthValue,
    setTimeValue,
    timeZone,
  ]);
};
